import * as React from "react";

import { View } from "@com.mgmtp.a12.client/client-core/lib/core/view";
import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import { BulletList } from "@com.mgmtp.a12.widgets/widgets-core/lib/bullet-list";
import { MailtoLink } from "@com.mgmtp.a12.widgets/widgets-core/lib/link";
import { Typography } from "@com.mgmtp.a12.widgets/widgets-core/lib/typography";

import { SimpleContentBox } from "../../component/SimpleContentBox";
import { RESOURCE_KEYS } from "../../localization";

const ACCESSIBILITY_RESOURCE_KEYS = RESOURCE_KEYS.application.view.accessibility;

export const AccessibilityPage: React.ComponentType<View> = function AccessibilityPage(props) {
	const { localizer } = React.useContext(LocalizerContext);
	const ariaLevel = props.ariaLevel ?? 1;

	return (
		<SimpleContentBox
			activityId={props.activityId}
			title={localizer({ key: ACCESSIBILITY_RESOURCE_KEYS.title })}
			closeButtonTitle={localizer({ key: ACCESSIBILITY_RESOURCE_KEYS.buttons.close.title })}
			ariaLevel={ariaLevel}
		>
			<Typography.Section>
				<Typography.Body>
					<p>
						MODUL-F legt großen Wert darauf, ihre Anwendungsseiten des Fachverfahrens- Portals im Einklang
						mit den Bestimmungen des Behindertengleichstellungsgesetzes des Bundes (<strong>BGG</strong>)
						sowie der Barrierefreien-Informationstechnik-Verordnung (<strong>BITV 2.0</strong>) zur
						Umsetzung der Richtlinie (EU) 2016/2102 barrierefrei zugänglich zu machen.
					</p>
					<p>Diese Erklärung wurde am 21. Juni erstellt.</p>
				</Typography.Body>
				<Typography.Headline level={2} ariaLevel={ariaLevel + 1}>
					Stand der Vereinbarkeit mit den Anforderungen
				</Typography.Headline>
				<Typography.Body>
					<p>Die Überprüfung der Einhaltung der Anforderungen beruht auf</p>
					<BulletList.Unordered>
						<BulletList.Item>
							einer im Zeitraum von Februar bis Juni durchgeführten, entwicklungsbegleitenden
							Selbstbewertung anhand der BITV-Kriterien bzw. der Kriterien der EN 301549
						</BulletList.Item>
					</BulletList.Unordered>
					<p>
						Aufgrund der Überprüfung ist die Website mit den zuvor genannten Anforderungen wegen der
						folgenden Ausnahmen teilweise vereinbar.
					</p>
				</Typography.Body>
				<Typography.Headline level={3} ariaLevel={ariaLevel + 2}>
					Nicht barrierefreie Inhalte
				</Typography.Headline>
				<Typography.Body>
					<p>Die nachstehend aufgeführten Inhalte sind aus folgenden Gründen nicht barrierefrei:</p>
					<BulletList.Unordered>
						<BulletList.Item>
							Der Fokus wird nicht immer korrekt gesetzt:
							<BulletList.Unordered>
								<BulletList.Item>
									Beim Öffnen oder Schließen neuer Detailanzeigen. Die Detailanzeigen sind visuell
									rechts neben der Hauptanzeige bzw. in der linearen Lesereihenfolge nach der
									Hauptanzeige angeordnet.
								</BulletList.Item>
								<BulletList.Item>Beim Ladeprozess.</BulletList.Item>
								<BulletList.Item>
									Damit ist Erfolgskriterium 9.2.4.3 Fokus-Reihenfolge der EN 301549 bzw. Kriterium
									2.4.3 der WCAG 2.1 nicht voll erfüllt.
								</BulletList.Item>
							</BulletList.Unordered>
						</BulletList.Item>
						<BulletList.Item>
							Einige Tabellen der Anwendung besitzen interaktive Zeilen. Mit Klick oder Enter wird der
							jeweilige Tabelleneintrag geöffnet. Aber bei einigen Tabellen werden die Zeilen
							fälschlicherweise als interaktiv ausgegeben und erhalten den Tastaturfokus. Dadurch sind die
							Erfolgskriterien 9.2.4.7 Fokus sichtbar und 9.4.1.2 Name, Rolle, Wert der EN 301549 bzw.
							Kriterien 2.4.7 und 4.1.2 der WCAG 2.1 verletzt.
						</BulletList.Item>
						<BulletList.Item>
							Manche Fachverfahren bieten die Möglichkeit, mehrere Dokumente gleichzeitig anzuhängen. In
							der Tabelle dieser Dokumente werden die Grafiken der Dokument-Typen für Textdokumente nicht
							vorgelesen. Damit ist das Erfolgskriterium 9.1.1.1 Nicht-Text-Inhalt der EN 301549 bzw.
							Kriterium 1.1.1 der WCAG 2.1 verletzt.
						</BulletList.Item>
					</BulletList.Unordered>
				</Typography.Body>
				<Typography.Headline level={3} ariaLevel={ariaLevel + 2}>
					Besonderheit der Tastatur-Bedienung des Filters
				</Typography.Headline>
				<Typography.Body>
					<p>
						Bei der Filterauswahl gibt es einen Bereich (visuell links) mit den einzelnen Filtern und einen
						Bereich (visuell rechts) mit den jeweiligen Filteroptionen. Wenn der Fokus auf einem Filter
						sitzt, dann können mit ENTER oder Leertaste die Filteroptionen aufgerufen werden. Mit ENTER wird
						dabei der Filter selbst noch nicht ausgewählt, mit der Leertaste wird er ausgewählt (Ausnahme:
						mit den verschiedenen Screenreadern kann dieses Verhalten abweichen). In beiden Fällen wird der
						Fokus auf die Filteroptionen gesetzt. Mit ESC kann der Fokus zurück auf den Filter gesetzt
						werden. Mit der Auswahl einer Filteroption wird der Filter automatisch auf
						&bdquo;ausgewählt&ldquo; gesetzt.
					</p>
				</Typography.Body>
				<Typography.Headline level={2} ariaLevel={ariaLevel + 1}>
					Durchsetzungsverfahren
				</Typography.Headline>
				<Typography.Body>
					<p>
						Im Rahmen eines Durchsetzungsverfahrens haben Sie die Möglichkeit, online einen Antrag auf
						Prüfung der Einhaltung der Anforderungen an die Barrierefreiheit zu stellen.
					</p>
					<p>
						Hamburger IT-Schlichtungsstelle
						<br />
						c/o Handelskammer Hamburg
						<br />
						Geschäftsbereich Recht & Fair Play
						<br />
						Adolphsplatz 1<br />
						20457 Hamburg
					</p>
				</Typography.Body>
				<Typography.Headline level={2} ariaLevel={ariaLevel + 1}>
					Schlichtungsverfahren
				</Typography.Headline>
				<Typography.Body>
					<p>
						Wenn auch nach Ihrem Feedback an den oben genannten Kontakt keine zufriedenstellende Lösung
						gefunden wurde, können Sie sich an die Schlichtungsstelle nach Paragraph 13 a HmbBGG wenden. Die
						Schlichtungsstelle hat die Aufgabe, bei Konflikten zum Thema Barrierefreiheit zwischen Menschen
						mit Behinderungen und öffentlichen Stellen eine außergerichtliche Streitbeilegung zu
						unterstützen. Das Schlichtungsverfahren ist kostenlos. Es muss kein Rechtsbeistand eingeschaltet
						werden.
					</p>
					<p>
						Zur Zeit übernimmt die Ombudsstelle der Senatskanzlei die Tätigkeiten der noch einzurichtenden
						Schlichtungsstelle.
					</p>
				</Typography.Body>
				<Typography.Headline level={3} ariaLevel={ariaLevel + 2}>
					Kontaktmöglichkeiten
				</Typography.Headline>
				<Typography.Body>
					<p>
						E-Mail:{" "}
						<MailtoLink to="ombudsstelle.barrierefreie-it@sk.hamburg.de">
							ombudsstelle.barrierefreie-it@sk.hamburg.de
						</MailtoLink>
						<br />
						Telefonische Sprechzeiten
						<br />
						Tel: (040) 428 23 2057
						<br />
						Mo.: 10.00 – 11.00 Uhr
					</p>
				</Typography.Body>
			</Typography.Section>
		</SimpleContentBox>
	);
};
